// CommandList — 모드별 결과 렌더 컴포넌트 (U48 + U49)
//
// 책임:
//   - 'command' 모드: 정적 명령 카탈로그 필터·렌더
//   - 'search' 모드: debounced query → useItems 결과 렌더 (limit=8)
//   - 'navigate' 모드: 그룹·태그 fuzzy 매칭 렌더
//   - 'mixed' 모드: 명령 + 그룹/태그 혼합 렌더 (items 미포함)
//   - 빈 입력(mixed + 텍스트 없음) 시: 최근 사용 5개 노출 (U49)
//   - 키보드 포커스 하이라이트 + 마우스 hover 동기화
//   - 결과 수를 상위 hook에 보고 (setResultCount)

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FolderPlus, FileText, Tag, Settings, Lock,
  Trash2, AlignJustify, Search, X, ChevronRight,
  Folder, Clock,
} from 'lucide-react';
import { useItems } from '../../hooks/useItems';
import { useGroups } from '../../hooks/useGroups';
import { useTags } from '../../hooks/useTags';
import { useDensity } from '../../hooks/useDensity';
import type { CommandPaletteMode } from '../../hooks/useCommandPalette';
import type { DensityMode } from '../../utils/density';
import type { RecentEntry, UseCmdkHistoryReturn } from '../../hooks/useCmdkHistory';

// ─── 명령 카탈로그 타입 ────────────────────────────────────────

export type CommandActionType =
  | 'navigate'
  | 'event'
  | 'density';

export interface CommandItem {
  id: string;
  /** 표시 라벨 */
  label: string;
  /** 부가 설명 (선택) */
  description?: string;
  /** lucide 아이콘 컴포넌트 */
  icon: React.ReactNode;
  /** 단축키 표시 (선택) */
  shortcut?: string;
  /** 액션 타입 */
  actionType: CommandActionType;
  /** navigate: 이동할 경로 */
  path?: string;
  /** event: 발행할 CustomEvent 이름 */
  eventName?: string;
  /** density: 변경할 밀도 값 */
  densityValue?: DensityMode;
  /** 그룹 이름 */
  group: string;
}

// ─── 정적 명령 카탈로그 ───────────────────────────────────────

/** CustomEvent 이름 — 다른 컴포넌트에서 수신 */
export const CMD_EVENT_NEW_CREDENTIAL = 'locker:cmd-new-credential';
export const CMD_EVENT_NEW_NOTE = 'locker:cmd-new-note';
export const CMD_EVENT_NEW_GROUP = 'locker:cmd-new-group';
export const CMD_EVENT_LOCK = 'locker:cmd-lock';
export const CMD_EVENT_SEARCH_RESET = 'locker:cmd-search-reset';

/** 전체 명령 카탈로그 */
export const COMMAND_CATALOG: CommandItem[] = [
  // 생성
  {
    id: 'new-credential',
    label: '새 크리덴셜',
    description: '새 비밀번호·API키·서버 등 추가',
    icon: <Plus size={16} />,
    shortcut: 'N',
    actionType: 'event',
    eventName: CMD_EVENT_NEW_CREDENTIAL,
    group: '생성',
  },
  {
    id: 'new-group',
    label: '새 그룹',
    description: '크리덴셜을 묶을 폴더 추가',
    icon: <FolderPlus size={16} />,
    actionType: 'event',
    eventName: CMD_EVENT_NEW_GROUP,
    group: '생성',
  },
  {
    id: 'new-note',
    label: '새 노트',
    description: '마크다운 노트 추가',
    icon: <FileText size={16} />,
    actionType: 'event',
    eventName: CMD_EVENT_NEW_NOTE,
    group: '생성',
  },

  // 이동
  {
    id: 'go-tags',
    label: '태그 관리',
    description: '/tags 페이지로 이동',
    icon: <Tag size={16} />,
    actionType: 'navigate',
    path: '/tags',
    group: '이동',
  },
  {
    id: 'go-settings',
    label: '설정',
    description: '보안·계정 설정으로 이동',
    icon: <Settings size={16} />,
    actionType: 'navigate',
    path: '/settings/security',
    group: '이동',
  },
  {
    id: 'go-trash',
    label: '휴지통',
    description: '삭제된 항목 보기',
    icon: <Trash2 size={16} />,
    actionType: 'navigate',
    path: '/?trash=1',
    group: '이동',
  },

  // 설정
  {
    id: 'density-compact',
    label: '표시 밀도: Compact',
    description: '항목을 조밀하게 표시',
    icon: <AlignJustify size={16} />,
    actionType: 'density',
    densityValue: 'compact',
    group: '표시',
  },
  {
    id: 'density-default',
    label: '표시 밀도: Default',
    description: '기본 간격으로 표시',
    icon: <AlignJustify size={16} />,
    actionType: 'density',
    densityValue: 'default',
    group: '표시',
  },
  {
    id: 'density-comfortable',
    label: '표시 밀도: Comfortable',
    description: '넓은 간격으로 표시',
    icon: <AlignJustify size={16} />,
    actionType: 'density',
    densityValue: 'comfortable',
    group: '표시',
  },

  // 액션
  {
    id: 'search-reset',
    label: '검색 초기화',
    description: '현재 검색 필터 제거',
    icon: <Search size={16} />,
    actionType: 'event',
    eventName: CMD_EVENT_SEARCH_RESET,
    group: '액션',
  },
  {
    id: 'lock',
    label: '잠금 (로그아웃)',
    description: '세션을 종료하고 잠금 화면으로',
    icon: <Lock size={16} />,
    actionType: 'navigate',
    path: '/lock',
    group: '액션',
  },
];

// ─── fuzzy 매칭 유틸 ─────────────────────────────────────────

/**
 * query가 text에 부분 일치하는지 확인 (대소문자 무시, 한국어 포함).
 */
export function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

// ─── 결과 항목 타입 ───────────────────────────────────────────

export type ResultItemKind = 'command' | 'item' | 'group' | 'tag';

export interface ResultEntry {
  id: string;
  kind: ResultItemKind;
  label: string;
  description?: string;
  icon: React.ReactNode;
  shortcut?: string;
  /** 클릭 시 실행할 액션 */
  onAction: () => void;
  /** 우측 X 버튼 클릭 시 항목 제거 (최근 항목 전용, U49) */
  onRemove?: () => void;
}

// ─── 그룹 섹션 헤더 컴포넌트 ─────────────────────────────────

interface SectionHeaderProps {
  label: string;
}

function SectionHeader({ label }: SectionHeaderProps) {
  return (
    <div className="cmd-list-section-header" role="presentation">
      {label}
    </div>
  );
}

// ─── 단일 결과 행 컴포넌트 ───────────────────────────────────

interface ResultRowProps {
  entry: ResultEntry;
  /** 현재 키보드 포커스 여부 */
  focused: boolean;
  /** 마우스 진입 시 포커스 전달 */
  onMouseEnter: () => void;
  /** ref를 외부에 전달 (스크롤 인투 뷰) */
  rowRef?: React.RefCallback<HTMLDivElement>;
}

function ResultRow({ entry, focused, onMouseEnter, rowRef }: ResultRowProps) {
  return (
    <div
      ref={rowRef}
      role="option"
      aria-selected={focused}
      className={['cmd-list-row', focused ? 'cmd-list-row--focused' : ''].filter(Boolean).join(' ')}
      onClick={entry.onAction}
      onMouseEnter={onMouseEnter}
      data-testid={`cmd-row-${entry.id}`}
    >
      {/* 아이콘 */}
      <span className="cmd-list-row__icon" aria-hidden="true">
        {entry.icon}
      </span>

      {/* 라벨 + 설명 */}
      <span className="cmd-list-row__content">
        <span className="cmd-list-row__label">{entry.label}</span>
        {entry.description && (
          <span className="cmd-list-row__desc">{entry.description}</span>
        )}
      </span>

      {/* 단축키 */}
      {entry.shortcut && (
        <span className="cmd-list-row__shortcut" aria-label={`단축키 ${entry.shortcut}`}>
          {entry.shortcut}
        </span>
      )}

      {/* 최근 항목 개별 제거 버튼 (U49) */}
      {entry.onRemove ? (
        <button
          type="button"
          className="cmd-list-row__remove"
          aria-label={`${entry.label} 최근 항목 제거`}
          tabIndex={-1}
          onClick={(e) => {
            // 행 클릭 이벤트 전파 차단 (실행 방지)
            e.stopPropagation();
            entry.onRemove?.();
          }}
          data-testid={`cmd-recent-remove-${entry.id}`}
        >
          <X size={12} />
        </button>
      ) : (
        /* 화살표 */
        <span className="cmd-list-row__arrow" aria-hidden="true">
          <ChevronRight size={12} />
        </span>
      )}
    </div>
  );
}

// ─── CommandList Props ────────────────────────────────────────

export interface CommandListProps {
  mode: CommandPaletteMode;
  /** prefix 제거된 실제 검색어 */
  searchTerm: string;
  /** 200ms debounce된 검색어 (items API 호출용) */
  debouncedSearchTerm: string;
  /** 현재 키보드 포커스 인덱스 */
  focusIndex: number;
  /** 포커스 인덱스 변경 (hover 동기화) */
  onFocusChange: (i: number) => void;
  /** 결과 수 변경 → 상위 hook에 보고 */
  onResultCountChange: (n: number) => void;
  /** 명령 실행 후 팔레트 닫기 */
  onClose: () => void;
  /** 최근 사용 기록 훅 인스턴스 (U49) */
  history: UseCmdkHistoryReturn;
}

// ─── CommandList 구현 ─────────────────────────────────────────

/**
 * 팔레트 모드별 결과 리스트 렌더.
 * ResultEntry 배열을 조합해 섹션 헤더 + 행 구조로 표시.
 *
 * 빈 입력 상태(mixed 모드 + searchTerm 비어있음)에서는
 * 최근 사용 5개를 노출한다 (U49).
 */
export function CommandList({
  mode,
  searchTerm,
  debouncedSearchTerm,
  focusIndex,
  onFocusChange,
  onResultCountChange,
  onClose,
  history,
}: CommandListProps) {
  const navigate = useNavigate();
  const { setDensity } = useDensity();

  // ── 검색 모드: items API ───────────────────────────────────
  const { data: itemsData } = useItems(
    mode === 'search' && debouncedSearchTerm.trim().length > 0
      ? { q: debouncedSearchTerm.trim(), limit: 8 }
      : { limit: 0 }, // limit=0이면 서버에서 빈 목록 반환 또는 사용하지 않음
  );

  // ── 이동 모드 + mixed: 그룹·태그 ─────────────────────────
  const { data: groups } = useGroups();
  const { data: tagsData } = useTags(
    (mode === 'navigate' || mode === 'mixed') ? searchTerm.trim() : '',
    10,
  );

  // ── 명령 실행 헬퍼 ────────────────────────────────────────
  const executeCommand = useCallback(
    (cmd: CommandItem) => {
      switch (cmd.actionType) {
        case 'navigate':
          if (cmd.path) navigate(cmd.path);
          break;
        case 'event':
          if (cmd.eventName) {
            window.dispatchEvent(new CustomEvent(cmd.eventName));
          }
          break;
        case 'density':
          if (cmd.densityValue) setDensity(cmd.densityValue);
          break;
      }
      // 최근 사용 기록에 추가 (U49)
      history.push({
        kind: 'command',
        label: cmd.label,
        payload: { commandId: cmd.id },
      });
      onClose();
    },
    [navigate, setDensity, history, onClose],
  );

  // ── 최근 항목 재실행 헬퍼 (U49) ──────────────────────────
  /**
   * 최근 항목을 클릭했을 때 해당 kind에 맞는 동작을 재실행한다.
   */
  const executeRecent = useCallback(
    (entry: RecentEntry) => {
      switch (entry.kind) {
        case 'command': {
          // commandId로 카탈로그 검색 후 실행
          const cmd = COMMAND_CATALOG.find((c) => c.id === entry.payload.commandId);
          if (cmd) executeCommand(cmd);
          break;
        }
        case 'search': {
          // 검색 결과 클릭 → itemId로 이동
          if (entry.payload.itemId !== undefined) {
            navigate(`/?item=${entry.payload.itemId}`);
          } else if (entry.payload.query) {
            // 검색어 재검색은 지원하지 않음 — 그냥 닫기
          }
          onClose();
          break;
        }
        case 'navigate': {
          if (entry.payload.groupId !== undefined) {
            navigate(`/?group=${entry.payload.groupId}`);
          } else if (entry.payload.tagName) {
            navigate(`/?tag=${encodeURIComponent(entry.payload.tagName)}`);
          }
          onClose();
          break;
        }
      }
    },
    [executeCommand, navigate, onClose],
  );

  // ── 빈 입력 시 최근 항목 표시 여부 (U49) ──────────────
  // mixed 모드이고 searchTerm이 비어있을 때만 최근 항목을 노출한다
  const isEmptyQuery = mode === 'mixed' && searchTerm.trim() === '';

  // 최근 항목 ResultEntry 변환
  const recentResults: ResultEntry[] = isEmptyQuery
    ? history.entries.map((entry, idx) => ({
        id: `recent-${idx}`,
        kind: 'command' as ResultItemKind,
        label: entry.label,
        description: entry.kind === 'command'
          ? '최근 명령'
          : entry.kind === 'search'
            ? '최근 검색'
            : '최근 이동',
        icon: <Clock size={16} />,
        onAction: () => executeRecent(entry),
        /** 최근 항목 제거 콜백 — ResultRow에서 X 버튼에 연결 */
        onRemove: () => history.remove(idx),
      }))
    : [];

  // ── 결과 배열 조합 ────────────────────────────────────────

  // command 모드: 카탈로그 필터
  const filteredCommands: CommandItem[] = (() => {
    if (mode !== 'command' && mode !== 'mixed') return [];
    const q = searchTerm.trim().toLowerCase();
    return COMMAND_CATALOG.filter(
      (c) =>
        !q ||
        c.label.toLowerCase().includes(q) ||
        (c.description?.toLowerCase().includes(q) ?? false),
    );
  })();

  // search 모드: items 결과
  const itemResults: ResultEntry[] = (() => {
    if (mode !== 'search') return [];
    if (!debouncedSearchTerm.trim()) return [];
    return (itemsData?.items ?? []).map((item) => ({
      id: `item-${item.id}`,
      kind: 'item' as ResultItemKind,
      label: item.name,
      description: item.type,
      icon: <Search size={16} />,
      onAction: () => {
        // 최근 사용 기록에 추가 (U49)
        history.push({
          kind: 'search',
          label: item.name,
          payload: { itemId: item.id, query: debouncedSearchTerm.trim() },
        });
        navigate(`/?item=${item.id}`);
        onClose();
      },
    }));
  })();

  // navigate / mixed 모드: 그룹 fuzzy 매칭
  const groupResults: ResultEntry[] = (() => {
    if (mode !== 'navigate' && mode !== 'mixed') return [];
    const q = searchTerm.trim();
    return (groups ?? [])
      .filter((g) => fuzzyMatch(q, g.name))
      .slice(0, 6)
      .map((g) => ({
        id: `group-${g.id}`,
        kind: 'group' as ResultItemKind,
        label: g.name,
        description: '그룹으로 이동',
        icon: <Folder size={16} />,
        onAction: () => {
          // 최근 사용 기록에 추가 (U49)
          history.push({
            kind: 'navigate',
            label: g.name,
            payload: { groupId: g.id },
          });
          navigate(`/?group=${g.id}`);
          onClose();
        },
      }));
  })();

  // navigate / mixed 모드: 태그 fuzzy 매칭
  const tagResults: ResultEntry[] = (() => {
    if (mode !== 'navigate' && mode !== 'mixed') return [];
    return (tagsData ?? [])
      .slice(0, 6)
      .map((t) => ({
        id: `tag-${t.id}`,
        kind: 'tag' as ResultItemKind,
        label: t.name,
        description: '태그로 필터',
        icon: <Tag size={16} />,
        onAction: () => {
          // 최근 사용 기록에 추가 (U49)
          history.push({
            kind: 'navigate',
            label: t.name,
            payload: { tagName: t.name },
          });
          navigate(`/?tag=${encodeURIComponent(t.name)}`);
          onClose();
        },
      }));
  })();

  // ── 전체 결과 플랫 배열 ───────────────────────────────────
  // 빈 입력: 최근 항목만 노출
  // 그 외: 명령 섹션 + items 섹션 + 그룹 섹션 + 태그 섹션 순서
  const allEntries: ResultEntry[] = isEmptyQuery
    ? recentResults
    : [
        ...filteredCommands.map((c) => ({
          id: c.id,
          kind: 'command' as ResultItemKind,
          label: c.label,
          description: c.description,
          icon: c.icon,
          shortcut: c.shortcut,
          onAction: () => executeCommand(c),
        })),
        ...itemResults,
        ...groupResults,
        ...tagResults,
      ];

  // 결과 수 상위 보고
  useEffect(() => {
    onResultCountChange(allEntries.length);
  }, [allEntries.length, onResultCountChange]);

  // ── 포커스된 행 스크롤 인투 뷰 ───────────────────────────
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (focusIndex >= 0) {
      const el = rowRefs.current.get(focusIndex);
      // jsdom 환경(테스트)에서는 scrollIntoView가 구현되지 않을 수 있으므로 guard
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusIndex]);

  // ── 빈 결과 ──────────────────────────────────────────────
  if (allEntries.length === 0) {
    // 빈 입력(최근 항목 없음) — 힌트 표시
    if (isEmptyQuery) {
      return (
        <div className="cmd-list-empty">
          <span>최근 사용 기록이 없습니다</span>
        </div>
      );
    }
    // search 모드에서 debounce 대기 중이거나 debouncedSearchTerm이 비어 있으면 힌트 표시
    if (mode === 'search' && !debouncedSearchTerm.trim()) {
      return (
        <div className="cmd-list-empty">
          <span>검색어를 입력하세요</span>
        </div>
      );
    }
    return (
      <div className="cmd-list-empty">
        <X size={16} />
        <span>결과 없음</span>
      </div>
    );
  }

  // ── 명령 섹션 그룹화 (command / mixed 모드) ───────────────
  // 명령 카탈로그를 group 속성으로 묶어 섹션 헤더 출력
  const commandGroups = filteredCommands.reduce<Map<string, CommandItem[]>>(
    (acc, c) => {
      const list = acc.get(c.group) ?? [];
      list.push(c);
      acc.set(c.group, list);
      return acc;
    },
    new Map(),
  );

  // 전체 인덱스 카운터 (섹션 헤더 건너뛰고 ResultEntry 순서와 맞춤)
  let globalIdx = 0;

  return (
    <div className="cmd-list" role="listbox" aria-label="명령 팔레트 결과">
      {/* ── 최근 사용 섹션 (빈 입력 상태, U49) ──────────── */}
      {isEmptyQuery && recentResults.length > 0 && (
        <div className="cmd-list-section">
          <SectionHeader label="최근 사용" />
          {recentResults.map((entry) => {
            const idx = globalIdx++;
            return (
              <ResultRow
                key={entry.id}
                entry={entry}
                focused={focusIndex === idx}
                onMouseEnter={() => onFocusChange(idx)}
                rowRef={(el) => {
                  if (el) rowRefs.current.set(idx, el);
                  else rowRefs.current.delete(idx);
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── 명령 섹션 (command / mixed) ─────────────────── */}
      {!isEmptyQuery && (mode === 'command' || mode === 'mixed') && filteredCommands.length > 0 && (
        <>
          {Array.from(commandGroups.entries()).map(([groupName, items]) => (
            <div key={groupName} className="cmd-list-section">
              <SectionHeader label={groupName} />
              {items.map((c) => {
                const idx = globalIdx++;
                return (
                  <ResultRow
                    key={c.id}
                    entry={{
                      id: c.id,
                      kind: 'command',
                      label: c.label,
                      description: c.description,
                      icon: c.icon,
                      shortcut: c.shortcut,
                      onAction: () => executeCommand(c),
                    }}
                    focused={focusIndex === idx}
                    onMouseEnter={() => onFocusChange(idx)}
                    rowRef={(el) => {
                      if (el) rowRefs.current.set(idx, el);
                      else rowRefs.current.delete(idx);
                    }}
                  />
                );
              })}
            </div>
          ))}
        </>
      )}

      {/* ── 검색 결과 섹션 (search) ──────────────────────── */}
      {!isEmptyQuery && mode === 'search' && itemResults.length > 0 && (
        <div className="cmd-list-section">
          <SectionHeader label="검색 결과" />
          {itemResults.map((entry) => {
            const idx = globalIdx++;
            return (
              <ResultRow
                key={entry.id}
                entry={entry}
                focused={focusIndex === idx}
                onMouseEnter={() => onFocusChange(idx)}
                rowRef={(el) => {
                  if (el) rowRefs.current.set(idx, el);
                  else rowRefs.current.delete(idx);
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── 그룹 섹션 (navigate / mixed) ─────────────────── */}
      {!isEmptyQuery && groupResults.length > 0 && (
        <div className="cmd-list-section">
          <SectionHeader label="그룹" />
          {groupResults.map((entry) => {
            const idx = globalIdx++;
            return (
              <ResultRow
                key={entry.id}
                entry={entry}
                focused={focusIndex === idx}
                onMouseEnter={() => onFocusChange(idx)}
                rowRef={(el) => {
                  if (el) rowRefs.current.set(idx, el);
                  else rowRefs.current.delete(idx);
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── 태그 섹션 (navigate / mixed) ─────────────────── */}
      {!isEmptyQuery && tagResults.length > 0 && (
        <div className="cmd-list-section">
          <SectionHeader label="태그" />
          {tagResults.map((entry) => {
            const idx = globalIdx++;
            return (
              <ResultRow
                key={entry.id}
                entry={entry}
                focused={focusIndex === idx}
                onMouseEnter={() => onFocusChange(idx)}
                rowRef={(el) => {
                  if (el) rowRefs.current.set(idx, el);
                  else rowRefs.current.delete(idx);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
